import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  DialogActions,
  Button,
  Divider,
  Grid2,
  Stack,
  Card,
  Chip,
  LinearProgress,
  alpha,
  useTheme,
  Avatar,
} from '@mui/material';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import { DateTime } from 'luxon';
import API from '../../api/api';
import { useDispatch } from 'react-redux';
import { setSnackError } from '../../Reducers/snackErrors';

const StudentInfoSession = ({ sessionInfo, assessmentInfo, open, close }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [loadingAssessment, setLoadingAssessment] = useState(false);

  const closeDialog = () => {
    setSelectedSession(null);
    setSelectedAssessment(null);
    close();
  };

  const handleSessionClick = (row) => {
    setSelectedAssessment(null); // Clear assessment view
    setSelectedSession(row);
  };

  const handleAssessmentSearch = async (row) => {
    setSelectedSession(null); // Clear session view
    setLoadingAssessment(true);
    try {
      const response = await API.get('/student_assessment_search', {
        params: { student_assessment_id: row.id, easy_score: false },
      });
      if (response.data.data) {
        setSelectedAssessment(response.data.data);
      }
    } catch (error) {
      dispatch(setSnackError({ title: 'Error', body: 'Unable to fetch assessment details.' }));
    } finally {
      setLoadingAssessment(false);
    }
  };

  function DetailItem({ label, value, color = "text.primary" }) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
          {label}
        </Typography>
        <Typography variant="body2" color={color} sx={{ fontWeight: 500 }}>
          {value || 'N/A'}
        </Typography>
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Tutor Activity Logs</Typography>
          <Typography variant="body2" color="text.secondary">Comprehensive view of past sessions and performance evaluations</Typography>
        </Box>
      </DialogTitle>
      <Divider />
      
      <DialogContent sx={{ p: 0, height: '70vh' }}>
        <Grid2 container sx={{ height: '100%' }}>
          {/* Left Column: Two Tables */}
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto', p: 2 }}>
            
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventNoteIcon color="primary" fontSize="small" /> RECENT SESSIONS
            </Typography>
            <TableContainer component={Card} variant="outlined" sx={{ mb: 4 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'action.hover' }}>Date</TableCell>
                    <TableCell sx={{ bgcolor: 'action.hover' }}>Tutor</TableCell>
                    <TableCell sx={{ bgcolor: 'action.hover' }}>Program</TableCell>
                    <TableCell sx={{ bgcolor: 'action.hover' }} align="right">Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionInfo?.map((row) => (
                    <TableRow 
                      key={row.id} 
                      hover 
                      selected={selectedSession?.id === row.id}
                      onClick={() => handleSessionClick(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {DateTime.fromISO(row.session_date, { zone: 'utc' }).toFormat('MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2">{row.first_name} {row.last_name}</Typography>
                          {row.substitute && <Chip size="small" icon={<PublishedWithChangesIcon />} label="Sub" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
                        </Stack>
                      </TableCell>
                      <TableCell><Typography variant="caption">{row.program_name}</Typography></TableCell>
                      <TableCell align="right">{DateTime.fromFormat(row.start_time, "HH:mm:ss").toFormat("h:mm a")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="secondary" fontSize="small" /> ACADEMIC ASSESSMENTS
            </Typography>
            <TableContainer component={Card} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'action.hover' }}>Date</TableCell>
                    <TableCell sx={{ bgcolor: 'action.hover' }}>Title</TableCell>
                    <TableCell sx={{ bgcolor: 'action.hover' }} align="right">Performance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assessmentInfo?.map((assessment) => (
                    <TableRow 
                      key={assessment.id} 
                      hover 
                      selected={selectedAssessment?.[0]?.student_assessment_id === assessment.id}
                      onClick={() => handleAssessmentSearch(assessment)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{DateTime.fromISO(assessment.session_date).toFormat('MMM dd')}</TableCell>
                      <TableCell><Typography variant="body2" fontWeight={500}>{assessment.title}</Typography></TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" fontWeight={700}>{Math.round((assessment.score/assessment.max_score)*100)}%</Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={(assessment.score/assessment.max_score)*100} 
                            sx={{ flexGrow: 1, height: 6, borderRadius: 3 }} 
                            color="secondary"
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid2>

          {/* Right Column: Dynamic Detail View */}
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ bgcolor: alpha(theme.palette.action.hover, 0.2), overflowY: 'auto' }}>
            {loadingAssessment ? (
              <Box sx={{ p: 4, textAlign: 'center' }}><LinearProgress /></Box>
            ) : selectedSession ? (
              <Box sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}><HistoryEduIcon /></Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>Session Details</Typography>
                    <Typography variant="caption" color="text.secondary">ID: #{selectedSession.id}</Typography>
                  </Box>
                </Stack>
                <Grid2 container spacing={2}>
                  <Grid2 size={6}><DetailItem label="Program" value={selectedSession.program_name} /></Grid2>
                  <Grid2 size={6}><DetailItem label="Duration" value={`${selectedSession.session_duration} mins`} /></Grid2>
                  <Grid2 size={6}><DetailItem label="Attendance" value={selectedSession.absent ? "Absent" : "Present"} color={selectedSession.absent ? "error.main" : "success.main"} /></Grid2>
                  <Grid2 size={6}><DetailItem label="Size" value={`${selectedSession.student_count} Students`} /></Grid2>
                </Grid2>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={700}>INSTRUCTOR NOTES</Typography>
                <Typography variant="body2" sx={{ mt: 1, fontStyle: selectedSession.notes ? 'normal' : 'italic' }}>
                  {selectedSession.notes || "No notes recorded for this session."}
                </Typography>
              </Box>
            ) : selectedAssessment ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Assessment Analysis</Typography>
                <Typography variant="subtitle" fontWeight={700} gutterBottom>Questions and answers</Typography>
                <Stack spacing={2}>
                  {selectedAssessment.map((item, index) => (
                    <Card key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="caption" color="primary" fontWeight={800}>QUESTION {index + 1}</Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>{item.question}</Typography>
                      <Divider sx={{ my: 1 }} />
                      <Stack direction="row" justifyContent="space-between">
                        <Box>
                          <Typography variant="caption" color="text.secondary">STUDENT ANSWER</Typography>
                          <Typography variant="body2" fontWeight={700} color={item.is_correct ? "success.main" : "error.main"}>
                            {item.answer_text || item.choice_text}
                          </Typography>
                        </Box>
                        <Chip 
                          label={`${item.answer_points} / ${item.points} pts`} 
                          size="small" 
                          color={item.is_correct ? "success" : "error"} 
                        />
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </Box>
            ) : (
              <Box sx={{ p: 6, textAlign: 'center', opacity: 0.5 }}>
                <HistoryEduIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="body1">Select an item from the left to view in-depth details</Typography>
              </Box>
            )}
          </Grid2>
        </Grid2>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={closeDialog} variant="outlined" disableElevation>Close Window</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentInfoSession;